import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import i18n from '@/libs/i18n'
import { resolveIcon } from '@/utils/assetUtils'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useAssetColumns from './useAssetColumns'
import useAssetFilters from './useAssetFilters'

export default function useAssetList() {
  // Use toast
  const toast = useToast()

  // Usar o composable de filtros persistentes
  const {
    searchQuery,
    searchOwner,
    customerId,
    chosenStatus,
    showFilters,
    perPage,
    sortBy,
    sortDesc,
    assetTypeId,
    assetBrandId,
    assetGroupId,
    clearFilters,
    updateSearchOwner,
    updateCustomerId,
    updateAssetGroupId,
  } = useAssetFilters()

  // Usar o composable de colunas customizáveis e persistentes
  const {
    allColumns,
    availableColumns,
    orderedAvailableColumns,
    selectedColumns,
    tableColumns,
    columnOrder,
    selectAllColumns,
    deselectAllColumns,
    resetColumnsToDefault,
    moveColumn,
    moveColumnByKey,
  } = useAssetColumns()

  const refAssetListTable = ref(null)

  const perPageOptions = [10, 25, 50, 100]
  const totalAssets = ref(0)
  const currentPage = ref(1)
  const isSortDirDesc = ref(true)
  const statusFilter = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refAssetListTable.value ? refAssetListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalAssets.value,
    }
  })

  const refetchData = () => {
    refAssetListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, statusFilter, customerId, chosenStatus, assetBrandId, assetTypeId, assetGroupId], () => {
    refetchData()
  })

  // Status que são ocultos por padrão — só aparecem se o usuário os selecionar explicitamente no multiselect
  const HIDDEN_BY_DEFAULT_STATUSES = ['noconnection', 'neverconnected']

  /**
   * Monta a lista de work statuses para a query.
   * - Se o usuário selecionou status explicitamente, usa exatamente eles.
   * - Se nenhum status foi selecionado, exclui os status "sem conexão" e "nunca conectado"
   *   para que a listagem padrão mostre apenas ativos com dados relevantes.
   */
  const resolveWorkStatusList = () => {
    if (chosenStatus.value && chosenStatus.value.length > 0) {
      return chosenStatus.value
    }
    return null // a API usará exclusão de status via excludeStatuses
  }

  const resolveExcludeStatuses = () => {
    if (chosenStatus.value && chosenStatus.value.length > 0) {
      return null // usuário escolheu explicitamente, não excluir nada
    }
    return HIDDEN_BY_DEFAULT_STATUSES
  }

  const fetchAssets = (ctx, callback) => {
    store
      .dispatch('app-asset/fetchAssets', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        assetBrandId: assetBrandId.value,
        assetTypeId: assetTypeId.value,
        assetGroupId: assetGroupId.value,
        sortBy: sortBy.value,
        sortDesc: sortDesc.value,
        status: statusFilter.value,
        customerId: customerId.value,
        workStatusList: resolveWorkStatusList(),
        excludeStatuses: resolveExcludeStatuses(),
      })
      .then(response => {
        const assets = response.data.data

        callback(assets)
        totalAssets.value = response.data.totalItems
      })
      .catch(() => {
        callback([])
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t("Error fetching assets' list"),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveAssetStatusVariantAndIcon = status => {
    if (status === 'on') return { variant: 'light-success', icon: 'CheckCircleIcon' }
    if (status === 'off') return { variant: 'light-secondary', icon: 'MinusCircleIcon' }
    if (status === 'severe') return { variant: 'light-warning', icon: 'AlertCircleIcon' }
    if (status === 'iddle') return { variant: 'light-info', icon: 'AlertTriangleIcon' }
    if (status === 'noconnection') return { variant: 'light-primary', icon: 'SlashIcon' }
    if (status === 'failure') return { variant: 'light-danger', icon: 'XOctagonIcon' }
    return { variant: 'light-primary', icon: 'SlashIcon' }
  }

  const resolveModelAvatarVariant = status => {
    if (status === 'failure') return 'light-danger'
    if (status === 'off') return 'light-secondary'
    if (status === 'noconnection') return 'light'
    if (status === 'severe') return 'light-warning'
    if (status === 'iddle') return 'light-info'
    if (status === 'on') return 'light-success'
    return 'light-primary'
  }

  return {
    fetchAssets,
    tableColumns,
    perPage,
    currentPage,
    totalAssets,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refAssetListTable,
    sortDesc,
    statusFilter,
    resolveModelAvatarVariant,
    resolveAssetStatusVariantAndIcon,
    refetchData,
    resolveIcon,
    customerId,
    chosenStatus,
    showFilters,
    searchOwner,
    clearFilters,
    updateSearchOwner,
    updateCustomerId,
    updateAssetGroupId,
    assetTypeId,
    assetBrandId,
    assetGroupId,

    // Column management
    allColumns,
    availableColumns,
    orderedAvailableColumns,
    selectedColumns,
    columnOrder,
    selectAllColumns,
    deselectAllColumns,
    resetColumnsToDefault,
    moveColumn,
    moveColumnByKey,
  }
}
